export interface AssistantToolConfig {
  toolId: string;
  enabledOperations: string[];
  isActive: boolean;
}

export interface Assistant {
  id: string;
  name: string;
  description: string;
  instructions: string; // System prompt/contexto
  avatar?: string; // URL do avatar ou emoji
  aiProvider: string; // openai, anthropic, etc.
  modelId: string;
  isPublic: boolean; // Se é um template público ou privado do usuário
  createdBy: string; // user_id
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
  tags?: string[]; // Tags para categorização
  category?: string; // Categoria do assistente
  usageCount?: number; // Quantas vezes foi usado
  visibility?: 'private' | 'team' | 'shared'; // Visibilidade do assistente
  sharedWithUsers?: string[]; // IDs dos usuários com acesso (quando visibility = 'shared')
  isTemplate?: boolean; // Se é um template público do banco (assistant_templates)
  tools?: AssistantToolConfig[]; // Ferramentas habilitadas para este assistente
}

export interface AssistantTemplate {
  id: string;
  name: string;
  description: string;
  instructions: string;
  avatar: string;
  category: string;
  tags: string[];
  aiProvider?: string; // Provider de IA (openai, anthropic, google)
  modelId?: string; // Modelo específico
  suggestedProvider?: string; // DEPRECATED: usar aiProvider
  suggestedModel?: string; // DEPRECATED: usar modelId
  isPopular?: boolean;
  isFeatured?: boolean; // Se o template está em destaque
}

export interface AssistantConversation {
  id: string;
  assistantId: string;
  title: string;
  messages: AssistantMessage[];
  userId: string;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ToolCallResult {
  toolId: string;
  operationId: string;
  operationName: string;
  parameters: Record<string, any>;
  result?: any;
  error?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  /** Código HTTP da resposta (ex.: 200, 404, 500) quando disponível */
  httpStatus?: number;
  startedAt: Date;
  completedAt?: Date;
}

export interface AssistantMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: Date;
  assistantId?: string;
  conversationId?: string;
  attachments?: Array<{ name: string; size: number; type: string; url?: string; file?: File }>;
  messageType?: 'message' | 'tool' | 'final_answer';
  toolId?: string;
  toolName?: string;
  toolOperation?: string;
  toolParameters?: Record<string, any>;
  toolResult?: any;
  toolStatus?: 'running' | 'completed' | 'failed';
  /** Chamadas de ferramentas executadas (ex.: Saipos) — usado na UI para exibir Request/Response */
  toolCalls?: ToolCallResult[];
  generatedFiles?: Array<{ filename: string; content: string; contentType: string; size: number }>;
}

export interface AssistantCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

export const ASSISTANT_CATEGORIES: AssistantCategory[] = [
  {
    id: 'productivity',
    name: 'Produtividade',
    description: 'Assistentes para aumentar sua produtividade',
    icon: '⚡',
    color: 'blue'
  },
  {
    id: 'writing',
    name: 'Escrita',
    description: 'Assistentes especializados em redação e conteúdo',
    icon: '✍️',
    color: 'green'
  },
  {
    id: 'analysis',
    name: 'Análise',
    description: 'Assistentes para análise de dados e documentos',
    icon: '📊',
    color: 'purple'
  },
  {
    id: 'coding',
    name: 'Programação',
    description: 'Assistentes para desenvolvimento de software',
    icon: '💻',
    color: 'orange'
  },
  {
    id: 'business',
    name: 'Negócios',
    description: 'Assistentes para estratégia e gestão empresarial',
    icon: '💼',
    color: 'indigo'
  },
  {
    id: 'education',
    name: 'Educação',
    description: 'Assistentes para ensino e aprendizagem',
    icon: '🎓',
    color: 'yellow'
  },
  {
    id: 'creative',
    name: 'Criativo',
    description: 'Assistentes para criação e design',
    icon: '🎨',
    color: 'pink'
  },
  {
    id: 'support',
    name: 'Suporte',
    description: 'Assistentes para atendimento ao cliente',
    icon: '🎧',
    color: 'teal'
  }
];
